<template>
  <div class="jump-modal-sound-recording">
    <!-- 录音动态效果 - 放在录音按钮上方并居中 -->
    <div v-if="isRecording" class="recording-indicator">
      <div class="sound-waves">
        <span class="wave"></span>
        <span class="wave"></span>
        <span class="wave"></span>
        <span class="wave"></span>
        <span class="wave"></span>
      </div>
      <span class="recording-text">正在录音...</span>
    </div>

    <div class="recording-button-wrapper">
      <button
        v-if="!isRecording"
        @click="startRecording"
        class="start-btn"
        :disabled="isStartingRecording || isStoppingRecording"
        :title="startButtonTip"
        style="overflow: visible; position: relative;"
      >
        <i class="el-icon-microphone"></i>
        {{ startButtonText }}
      </button>
      <button
        v-else
        @click="stopRecording"
        class="end-btn"
        :disabled="isStoppingRecording || isStartingRecording"
      >
        <i class="el-icon-microphone"></i>
        结束录音
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JumpModalSoundRecording',
  props: {
    startButtonText: {
      type: String,
      default: '开始录音' // 明确指定默认文本为“开始录音”
    },
    startButtonTip: {
      type: String,
      default() {
        return this.startButtonText // 默认提示 = 按钮文本
      }
    },
    patientSelected: {
      type: Boolean,
      default: false
    },
    medmstrId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      mediaRecorder: null,
      audioChunks: [],
      audioBlob: null,
      isRecording: false,
      isStartingRecording: false,
      isStoppingRecording: false,
      lastClickTime: 0
    };
  },
  methods: {
    // 开始录音
    startRecording() {
      // 防重复提交
      if (this.isStartingRecording || this.isRecording || this.isStoppingRecording) {
        console.warn('录音正在启动中、已开始或正在停止，请勿重复点击');
        return;
      }

      // 防抖：500ms内只能点击一次
      const now = Date.now();
      if (now - this.lastClickTime < 500) {
        console.warn('操作过于频繁，请稍后再试');
        return;
      }
      this.lastClickTime = now;

      // 检查是否选择了患者
      if (!this.patientSelected && !this.medmstrId) {
        console.warn("请先选择一个患者");
        return;
      }

      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$message.error('当前浏览器不支持录音功能，请使用 HTTPS 协议访问');
        return;
      }

      // 设置启动状态
      this.isStartingRecording = true;

      navigator.mediaDevices
        .getUserMedia({ audio: true, video: false })
        .then(stream => {
          const options = {
            mimeType: 'audio/webm;codecs=opus',
            audioBitsPerSecond: 32000
          };

          if (MediaRecorder.isTypeSupported(options.mimeType)) {
            this.mediaRecorder = new MediaRecorder(stream, options);
          } else {
            this.mediaRecorder = new MediaRecorder(stream);
            console.warn('浏览器不支持指定的音频编码格式，使用默认配置');
          }

          this.audioChunks = [];
          this.mediaRecorder.ondataavailable = e => {
            this.audioChunks.push(e.data);
          };

          this.mediaRecorder.onstop = e => {
            this.audioBlob = new Blob(this.audioChunks, {
              type: "audio/webm"
            });

            // 检查文件大小
            const fileSizeMB = this.audioBlob.size / (1024 * 1024);
            console.log(`录音文件大小: ${fileSizeMB.toFixed(2)} MB`);

            this.isRecording = false;
            this.isStartingRecording = false;

            // 录音结束后自动识别
            if (this.audioBlob) {
              setTimeout(() => {
                this.handleShibie();
              }, 100);
            }
          };

          this.mediaRecorder.start();
          this.isRecording = true;
          this.isStartingRecording = false;

          console.log("开始录音");
        })
        .catch(error => {
          console.error("Error accessing the media devices.", error);
          this.isStartingRecording = false;
          this.$message.error('启动录音失败，请重试');
        });
    },

    // 结束录音
    stopRecording() {
      // 防重复提交
      if (this.isStoppingRecording || this.isStartingRecording || !this.isRecording) {
        console.warn('录音正在停止中、正在启动或未开始，请勿重复点击');
        return;
      }

      // 防抖：500ms内只能点击一次
      const now = Date.now();
      if (now - this.lastClickTime < 500) {
        console.warn('操作过于频繁，请稍后再试');
        return;
      }
      this.lastClickTime = now;

      this.isStoppingRecording = true;

      if (this.mediaRecorder && this.isRecording) {
        console.log("Stopping recording...");
        this.mediaRecorder.stop();
        this.isStoppingRecording = false;
        console.log("录音已停止");
      } else {
        this.isRecording = false;
        this.isStoppingRecording = false;
      }
    },

    // 识别录音
    async handleShibie() {
      if (this.audioBlob) {
        this.$emit("handleShibie", this.audioBlob);
      } else {
        console.log("没有录音可识别");
      }
    }
  },

  beforeDestroy() {
    // 清理资源
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
    }
  }
};
</script>

<style scoped>
.jump-modal-sound-recording {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* 关键修改：把 width: 100% 改为 max-width: 100% , 让组件支持自适应宽度*/
  max-width: 100%; 
  padding: 20px 0;
  /* 新增：确保组件层级正常 */
  position: relative;
  z-index: 1;
}

.recording-button-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  /* 新增：确保按钮容器层级正常 */
  position: relative;
  z-index: 2;
}

.recording-button-wrapper button {
  background-color: white !important;
  color: #4169e1 !important;
  border: 1px solid #d1d1d6 !important;
  border-radius: 9999px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: all 0.3s !important;
  cursor: pointer;
  font-weight: 500;
  line-height: 26px;
  font-size: 14px;
  padding: 0.25rem 22px;
  min-width: 144px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 关键修复：确保提示不被截断 */
  overflow: visible !important;
  /* 确保鼠标事件能触发 */
  pointer-events: auto !important;
}

.recording-button-wrapper button i {
  margin-right: 6px;
}

.recording-button-wrapper .end-btn {
  background: #d32f2f !important;
  color: #fff !important;
  line-height: 34px !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease !important;
}

.recording-button-wrapper .end-btn:hover:not(:disabled) {
  opacity: 0.9 !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 12px rgba(211, 47, 47, 0.4) !important;
}

.recording-button-wrapper .end-btn:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

.recording-button-wrapper .start-btn {
  background-color: #ff9500 !important;
  color: #fff !important;
  border-color: #ff9500 !important;
  box-shadow: 0 4px 12px rgba(255, 149, 0, 0.3) !important;
  line-height: 34px !important;
  transition: all 0.3s ease !important;
}

.recording-button-wrapper .start-btn:hover:not(:disabled) {
  opacity: 0.9 !important;
  transform: translateY(-2px) scale(1.08) !important;
  box-shadow: 0 6px 16px rgba(255, 149, 0, 0.4) !important;
}

.recording-button-wrapper .start-btn:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* 录音动态效果 */
.recording-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(65, 105, 225, 0.1) 0%, rgba(65, 105, 225, 0.05) 100%);
  border-radius: 12px;
  border: 1px solid rgba(65, 105, 225, 0.2);
}

.sound-waves {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.wave {
  width: 4px; 
  height: 20px;
  background: linear-gradient(to top, #4169e1, #6b9df5);
  border-radius: 2px;
  margin: 0 2px;
  animation: wave 1.2s ease-in-out infinite;
}

.wave:nth-child(1) { animation-delay: 0s; }
.wave:nth-child(2) { animation-delay: 0.1s; }
.wave:nth-child(3) { animation-delay: 0.2s; }
.wave:nth-child(4) { animation-delay: 0.3s; }
.wave:nth-child(5) { animation-delay: 0.4s; }

.recording-text {
  color: #4169e1;
  font-size: 14px;
  font-weight: 600;
}

@keyframes wave {
  0%, 100% {
    transform: scaleY(0.5);
    opacity: 0.6;
  }
  50% {
    transform: scaleY(1.2);
    opacity: 1;
  }
}
</style>
